/**
 * PropertySubscriptionProvider — composes the decomposed subscription data hooks
 * (query, pricing tiers, mutations, selectors) into the context value. The type
 * contracts and consumer hooks live in ./PropertySubscriptionContext.
 */

import React, { ReactNode, useCallback, useMemo } from "react";
import { useAuth } from "./AuthContext";
import {
  PropertySubscriptionContext,
  PropertySubscriptionContextType,
} from "./PropertySubscriptionContext";
import { useSubscriptionsQuery } from "./subscription/useSubscriptionsQuery";
import { useStableAccessToken } from "./subscription/useStableAccessToken";
import { usePricingTiers } from "./subscription/usePricingTiers";
import { useSubscriptionMutations } from "./subscription/useSubscriptionMutations";
import { useSubscriptionSelectors } from "./subscription/useSubscriptionSelectors";
import { storySessionKey } from "@/queries/storySessionKey";

interface PropertySubscriptionProviderProps {
  children: ReactNode;
}

export const PropertySubscriptionProvider: React.FC<
  PropertySubscriptionProviderProps
> = ({ children }) => {
  const { isAuthenticated, tokens, user } = useAuth();
  const accessToken = useStableAccessToken(tokens?.access ?? null);
  const sessionKey = storySessionKey(user?.id);

  const getStoredAccessToken = useCallback((): string | null => {
    return accessToken;
  }, [accessToken]);

  const {
    propertySubscriptions,
    summary,
    isLoading,
    hasAttemptedLoad,
    error,
    refreshSubscriptions,
  } = useSubscriptionsQuery({
    isAuthenticated,
    accessToken,
    sessionKey,
    getStoredAccessToken,
  });

  const { pricingTiers, loadPricingTiers } = usePricingTiers({
    isAuthenticated,
    accessToken,
    sessionKey,
    getStoredAccessToken,
  });

  const {
    addPropertyToList,
    cancelPropertySubscription,
    recordSimulationUsage,
  } = useSubscriptionMutations({ getStoredAccessToken, sessionKey });

  const {
    getPropertySubscription,
    canAccessFeature,
    canRunSimulation,
    getAnnualCostForProperties,
  } = useSubscriptionSelectors({ propertySubscriptions, user });

  const contextValue: PropertySubscriptionContextType = useMemo(
    () => ({
      propertySubscriptions,
      summary,
      pricingTiers,
      isLoading,
      hasAttemptedLoad,
      error,
      refreshSubscriptions,
      loadPricingTiers,
      addPropertyToList,
      cancelPropertySubscription,
      recordSimulationUsage,
      getPropertySubscription,
      canAccessFeature,
      canRunSimulation,
      getAnnualCostForProperties,
    }),
    [
      propertySubscriptions,
      summary,
      pricingTiers,
      isLoading,
      hasAttemptedLoad,
      error,
      refreshSubscriptions,
      loadPricingTiers,
      addPropertyToList,
      cancelPropertySubscription,
      recordSimulationUsage,
      getPropertySubscription,
      canAccessFeature,
      canRunSimulation,
      getAnnualCostForProperties,
    ],
  );

  return (
    <PropertySubscriptionContext.Provider value={contextValue}>
      {children}
    </PropertySubscriptionContext.Provider>
  );
};
